// src/pages/admin/CreateQuestionnaire.js
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const CreateQuestionnaire = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([
    { text: '', options: [{ text: '', isCorrect: true }, { text: '', isCorrect: false }] }
  ]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);

  const addQuestion = () => {
    const newQuestions = [
      ...questions,
      { text: '', options: [{ text: '', isCorrect: true }, { text: '', isCorrect: false }] }
    ];
    setQuestions(newQuestions);
    
    // Set the new question as active
    setActiveQuestionIndex(newQuestions.length - 1);
    
    // Scroll to the new question after a short delay
    setTimeout(() => {
      const newQuestionElement = document.getElementById(`question-${newQuestions.length - 1}`);
      if (newQuestionElement) {
        newQuestionElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const addOption = (questionIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options.push({ text: '', isCorrect: false });
    setQuestions(updatedQuestions);
  };

  const handleQuestionChange = (index, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index].text = value;
    setQuestions(updatedQuestions);
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex].text = value;
    setQuestions(updatedQuestions);
  };

  const handleCorrectOptionChange = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questions];
    // Reset all options to false first
    updatedQuestions[questionIndex].options.forEach(option => {
      option.isCorrect = false;
    });
    // Set the selected option to true
    updatedQuestions[questionIndex].options[optionIndex].isCorrect = true;
    setQuestions(updatedQuestions);
  };

  const removeQuestion = (index) => {
    if (questions.length > 1) {
      const updatedQuestions = [...questions];
      updatedQuestions.splice(index, 1);
      setQuestions(updatedQuestions);
      
      // Update active question index if needed
      if (activeQuestionIndex === index) {
        if (updatedQuestions.length > 0) {
          setActiveQuestionIndex(Math.min(index, updatedQuestions.length - 1));
        } else {
          setActiveQuestionIndex(null);
        }
      } else if (activeQuestionIndex > index) {
        setActiveQuestionIndex(activeQuestionIndex - 1);
      }
    }
  };

  const removeOption = (questionIndex, optionIndex) => {
    if (questions[questionIndex].options.length > 2) {
      const updatedQuestions = [...questions];
      
      // Check if we're removing the correct option
      const isRemovingCorrect = updatedQuestions[questionIndex].options[optionIndex].isCorrect;
      
      updatedQuestions[questionIndex].options.splice(optionIndex, 1);
      
      // If we removed the correct option, make the first option correct
      if (isRemovingCorrect && updatedQuestions[questionIndex].options.length > 0) {
        updatedQuestions[questionIndex].options[0].isCorrect = true;
      }
      
      setQuestions(updatedQuestions);
    }
  };

  // Toggle active question
  const toggleQuestion = (index) => {
    setActiveQuestionIndex(activeQuestionIndex === index ? null : index);
  };

  const validateForm = () => {
    if (!title.trim()) {
      setError('Title is required');
      return false;
    }

    for (let i = 0; i < questions.length; i++) {
      if (!questions[i].text.trim()) {
        setError(`Question ${i + 1} text is required`);
        setActiveQuestionIndex(i);
        return false;
      }

      let hasCorrectOption = false;
      for (let j = 0; j < questions[i].options.length; j++) {
        if (!questions[i].options[j].text.trim()) {
          setError(`Option ${j + 1} for Question ${i + 1} is required`);
          setActiveQuestionIndex(i);
          return false;
        }
        if (questions[i].options[j].isCorrect) {
          hasCorrectOption = true;
        }
      }

      if (!hasCorrectOption) {
        setError(`Question ${i + 1} must have a correct answer selected`);
        setActiveQuestionIndex(i);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      window.scrollTo(0, 0);
      return;
    }
    
    try {
      setSubmitting(true);
      await api.post('/questionnaires', {
        title,
        description,
        questions
      });
      setSuccess(true);
      
      // Scroll to top to show success message
      window.scrollTo(0, 0);
      
      // Navigate back to list after short delay
      setTimeout(() => {
        navigate('/questionnaires');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create questionnaire');
      window.scrollTo(0, 0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="questionnaire-creator">
      <div className="creator-header">
        <button 
          className="back-button"
          onClick={() => navigate('/questionnaires')}
        >
          ← Back to Quizes
        </button>
        
        <h1>Create New Quiz</h1>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">
        <span className="success-icon">✓</span> Quiz created successfully!
      </div>}
      
      <form onSubmit={handleSubmit}>
        <div className="creator-section">
          <div className="section-header">
            <h2>Basic Information</h2>
          </div>
          <div className="section-content">
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter Quiz title"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="3"
                placeholder="Enter a description for this Quiz"
              ></textarea>
            </div>
          </div>
        </div>
        
        <div className="creator-section">
          <div className="section-header questions-header">
            <h2>Questions</h2>
            <button 
              type="button" 
              className="add-button"
              onClick={addQuestion}
            >
              + Add Question
            </button>
          </div>
          
          <div className="section-content">
            {questions.length === 0 ? (
              <div className="empty-questions">
                <div className="empty-icon">?</div>
                <p>No questions added yet.</p>
                <button 
                  type="button" 
                  className="add-button"
                  onClick={addQuestion}
                >
                  + Add Your First Question
                </button>
              </div>
            ) : (
              <div className="questions-list">
                {questions.map((question, qIndex) => (
                  <div 
                    key={qIndex} 
                    className={`question-card ${activeQuestionIndex === qIndex ? 'active' : ''}`}
                    id={`question-${qIndex}`}
                  >
                    <div 
                      className="question-header"
                      onClick={() => toggleQuestion(qIndex)}
                    >
                      <div className="question-title">
                        <span className="question-number">{qIndex + 1}</span>
                        <h3>{question.text || `Question ${qIndex + 1}`}</h3>
                      </div>
                      <div className="question-actions">
                        <span className="options-count">
                          {question.options.length} options
                        </span>
                        <span className="expand-icon">
                          {activeQuestionIndex === qIndex ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                    
                    {activeQuestionIndex === qIndex && (
                      <div className="question-body">
                        <div className="form-group">
                          <label>Question Text</label>
                          <input
                            type="text"
                            value={question.text}
                            onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
                            placeholder="Enter your question here"
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <div className="options-header">
                            <label>Options</label>
                            <button 
                              type="button"
                              className="add-option-button"
                              onClick={() => addOption(qIndex)}
                            >
                              + Add Option
                            </button>
                          </div>
                          
                          <div className="options-list">
                            {question.options.map((option, oIndex) => (
                              <div key={oIndex} className="option-item">
                                <div className="option-content">
                                  <div className="radio-container">
                                    <input
                                      type="radio"
                                      name={`correct-${qIndex}`}
                                      checked={option.isCorrect}
                                      onChange={() => handleCorrectOptionChange(qIndex, oIndex)}
                                      id={`option-${qIndex}-${oIndex}`}
                                      required
                                    />
                                    <label 
                                      htmlFor={`option-${qIndex}-${oIndex}`}
                                      className="radio-label"
                                    >
                                      {option.isCorrect ? 'Correct' : ''}
                                    </label>
                                  </div>
                                  <div className="option-input-container">
                                    <input
                                      type="text"
                                      placeholder={`Option ${oIndex + 1}`}
                                      value={option.text}
                                      onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                                      required
                                    />
                                    <button 
                                      type="button"
                                      className="remove-option-button"
                                      disabled={question.options.length <= 2}
                                      onClick={() => removeOption(qIndex, oIndex)}
                                      title={question.options.length <= 2 ? "At least two options are required" : "Remove this option"}
                                    >
                                      ×
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="question-footer">
                          <button 
                            type="button"
                            className="remove-question-button"
                            onClick={() => removeQuestion(qIndex)}
                            disabled={questions.length <= 1}
                          >
                            Delete Question
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button"
            className="cancel-button"
            onClick={() => navigate('/questionnaires')}
          >
            Cancel
          </button>
          <button 
            type="submit"
            className="create-button"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner"></span>
                Creating...
              </>
            ) : 'Create Quiz'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateQuestionnaire;